import React, { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { authFetch } from "../utils/authFetch";

const CATEGORIE_ESCLUSE = new Set([
  "CONTENITORI",
  "OGGETTISTICA",
]);

const normalizza = (valore) =>
  String(valore || "").trim().toUpperCase();

const formatPrezzo = (valore) =>
  new Intl.NumberFormat("it-IT", {
    style: "currency",
    currency: "EUR",
  }).format(Number(valore) || 0);

function Freccia({ indietro = false }) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      width="20"
      height="20"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      {indietro ? (
        <>
          <path d="M19 12H5" />
          <path d="m12 19-7-7 7-7" />
        </>
      ) : (
        <>
          <path d="M5 12h14" />
          <path d="m12 5 7 7-7 7" />
        </>
      )}
    </svg>
  );
}

export default function AppAziendaCatalogoPage() {
  const navigate = useNavigate();

  const [prodotti, setProdotti] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [ricerca, setRicerca] = useState("");
  const [categoria, setCategoria] = useState("ALL");
  const [formato, setFormato] = useState("ALL");
  const [prodottoSelezionato, setProdottoSelezionato] = useState(null);

  useEffect(() => {
    let cancelled = false;

    async function loadProdotti() {
      try {
        setLoading(true);
        setError("");

        const response = await authFetch(
          "/api/prodotti/?scope=catalogo&tipo_listino=azienda"
        );

        const data = await response.json();

        if (!response.ok) {
          throw new Error(
            data?.detail || "Impossibile caricare il catalogo aziende."
          );
        }

        if (!cancelled) {
          setProdotti(Array.isArray(data) ? data : []);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err?.message || "Errore durante il caricamento del catalogo."
          );
          setProdotti([]);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadProdotti();

    return () => {
      cancelled = true;
    };
  }, []);

  const catalogoCommerciale = useMemo(
    () =>
      prodotti.filter(
        (prodotto) =>
          !CATEGORIE_ESCLUSE.has(normalizza(prodotto.category))
      ),
    [prodotti]
  );

  const categorie = useMemo(
    () =>
      Array.from(
        new Set(
          catalogoCommerciale
            .map((prodotto) => String(prodotto.category || "").trim())
            .filter(Boolean)
        )
      ).sort((a, b) => a.localeCompare(b)),
    [catalogoCommerciale]
  );

  const getListinoAzienda = (prodotto) =>
    (prodotto.listini || []).find(
      (listino) => normalizza(listino.tipo) === "AZIENDA"
    );

  const isDisponibile = (prodotto) => {
    const listino = getListinoAzienda(prodotto);
    return listino ? !!listino.disponibile : true;
  };

  const getPrezzo = (prodotto) => {
    const valori = [
      prodotto.prezzo,
      prodotto.price_azienda,
      getListinoAzienda(prodotto)?.prezzo,
    ];

    for (const valore of valori) {
      if (
        valore !== null &&
        valore !== undefined &&
        valore !== "" &&
        Number.isFinite(Number(valore))
      ) {
        return Number(valore);
      }
    }

    return 0;
  };

  const getQuantita = (prodotto) => {
    const tipo = prodotto.unit_type || prodotto.unitType;

    if (tipo === "litri") {
      return `${prodotto.litri_per_unita || 0} L`;
    }

    if (tipo === "pezzi") {
      return `${prodotto.pezzi_per_unita || 0} pz`;
    }

    return "";
  };

  const prodottiFiltrati = useMemo(() => {
    const query = ricerca.trim().toLowerCase();

    return catalogoCommerciale
      .filter((prodotto) => isDisponibile(prodotto))
      .filter((prodotto) => {
        if (!query) return true;

        return (
          String(prodotto.name || "").toLowerCase().includes(query) ||
          String(prodotto.category || "").toLowerCase().includes(query)
        );
      })
      .filter(
        (prodotto) =>
          categoria === "ALL" || prodotto.category === categoria
      )

      .filter((prodotto) =>
        formato === "ALL" ||
        (
          String(prodotto.unit_type || prodotto.unitType).toLowerCase() === "litri" &&
          Math.abs(Number(prodotto.litri_per_unita) - Number(formato)) < 0.0001
        )
      )
      .sort((a, b) =>
        String(a.name || "").localeCompare(String(b.name || ""))
      );
  }, [catalogoCommerciale, ricerca, categoria, formato]);

  const apriProdotto = (prodotto) => {
    setProdottoSelezionato(prodotto);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  const tornaAlCatalogo = () => {
    setProdottoSelezionato(null);
    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  if (prodottoSelezionato) {
    const prodotto = prodottoSelezionato;
    const descrizione = String(prodotto.description || "").trim();
    const quantita = getQuantita(prodotto);

    return (
      <div className="container py-4" style={{ maxWidth: 760 }}>
        <button
          type="button"
          className="btn btn-link text-decoration-none px-0 mb-4 d-inline-flex align-items-center gap-2"
          onClick={tornaAlCatalogo}
        >
          <Freccia indietro />
          Torna al catalogo
        </button>

        <div
          className="card border-0 shadow-sm"
          style={{ borderRadius: 20, overflow: "hidden" }}
        >
          {prodotto.image_url && (
            <div
              className="bg-light d-flex align-items-center justify-content-center"
              style={{ minHeight: 220 }}
            >
              <img
                src={prodotto.image_url}
                alt={prodotto.name || "Prodotto"}
                style={{
                  display: "block",
                  maxWidth: "100%",
                  width: "100%",
                  maxHeight: 350,
                  objectFit: "contain",
                  padding: 16,
                }}
              />
            </div>
          )}

          <div className="card-body p-4">
            <div className="small text-uppercase text-muted fw-bold mb-2">
              {prodotto.category || "Catalogo aziende"}
            </div>

            <h2 className="fw-bold mb-3">
              {prodotto.name || "Prodotto"}
            </h2>

            {quantita && (
              <div className="text-muted mb-3">
                Formato: {quantita}
              </div>
            )}

            <div
              className="rounded-4 p-3 mb-4"
              style={{ background: "#f6f7f8" }}
            >
              <div className="small text-muted mb-1">
                Prezzo listino aziende
              </div>
              <div className="fs-3 fw-bold">
                {formatPrezzo(getPrezzo(prodotto))}
              </div>
            </div>

            <h5 className="fw-bold mb-3">
              Scopri questo prodotto
            </h5>

            {descrizione ? (
              <div
                style={{
                  whiteSpace: "pre-line",
                  lineHeight: 1.8,
                  overflowWrap: "anywhere",
                }}
              >
                {descrizione}
              </div>
            ) : (
              <p className="text-muted mb-0">
                Descrizione non ancora disponibile.
              </p>
            )}
          </div>
        </div>

        <button
          type="button"
          className="btn btn-outline-dark rounded-pill w-100 mt-4 py-3 d-flex align-items-center justify-content-center gap-2"
          onClick={tornaAlCatalogo}
        >
          <Freccia indietro />
          Torna al catalogo
        </button>
      </div>
    );
  }

  return (
    <div className="container py-4" style={{ maxWidth: 760 }}>
      <button
        type="button"
        className="btn btn-link text-decoration-none px-0 mb-3 d-inline-flex align-items-center gap-2"
        onClick={() => navigate("/app")}
      >
        <Freccia indietro />
        Torna alla home
      </button>

      <div className="mb-4">
        <div className="small text-uppercase text-muted fw-bold">
          Area Azienda
        </div>

        <h2 className="fw-bold mb-1">Catalogo aziende</h2>

        <div className="text-muted">
          Consulta i prodotti disponibili e i prezzi riservati alla tua azienda.
        </div>
      </div>

      <div
        className="card border-0 shadow-sm mb-3"
        style={{ borderRadius: 18 }}
      >
        <div className="card-body p-3">
          <input
            type="search"
            className="form-control rounded-3 mb-3"
            placeholder="Cerca prodotto o categoria..."
            value={ricerca}
            onChange={(event) => setRicerca(event.target.value)}
          />

          <select
            className="form-select rounded-3"
            value={categoria}
            onChange={(event) => setCategoria(event.target.value)}
          >
            <option value="ALL">Tutte le categorie</option>

            {categorie.map((item) => (
              <option key={item} value={item}>
                {item}
              </option>
            ))}
          </select>

          <div className="mt-3">
            <div className="small fw-bold text-muted mb-2">
              Filtra per formato
            </div>
            <div className="d-flex flex-wrap gap-2">
              {[
                ["ALL", "Tutti"],
                ["0.75", "0,75 L"],
                ["1", "1 L"],
                ["20", "20 L"],
                ["25", "25 L"],
              ].map(([valore, etichetta]) => (
                <button
                  key={valore}
                  type="button"
                  className={`btn btn-sm rounded-pill ${
                    formato === valore ? "btn-dark" : "btn-outline-secondary"
                  }`}
                  aria-pressed={formato === valore}
                  onClick={() => setFormato(valore)}
                >
                  {etichetta}
                </button>
              ))}
            </div>
          </div>
        </div>
      </div>

      {loading && (
        <div className="text-center py-5">
          <div className="spinner-border" role="status" />
          <div className="text-muted mt-3">
            Caricamento catalogo...
          </div>
        </div>
      )}

      {error && (
        <div className="alert alert-danger rounded-4" role="alert">
          {error}
        </div>
      )}

      {!loading && !error && prodottiFiltrati.length === 0 && (
        <div className="text-center text-muted py-5">
          Nessun prodotto disponibile.
        </div>
      )}

      {!loading && !error && prodottiFiltrati.length > 0 && (
        <div className="d-flex flex-column gap-3">
          {prodottiFiltrati.map((prodotto) => {
            const quantita = getQuantita(prodotto);

            return (
              <button
                key={prodotto.id}
                type="button"
                className="card border-0 shadow-sm w-100 text-start"
                style={{
                  borderRadius: 18,
                  overflow: "hidden",
                  cursor: "pointer",
                  background: "#fff",
                  color: "inherit",
                }}
                onClick={() => apriProdotto(prodotto)}
                aria-label={`Apri dettagli di ${prodotto.name || "prodotto"}`}
              >
                <div className="card-body p-3 w-100">
                  <div className="d-flex gap-3 align-items-center">
                    {prodotto.image_url && (
                      <img
                        src={prodotto.image_url}
                        alt={prodotto.name || "Prodotto"}
                        style={{
                          width: 78,
                          height: 78,
                          objectFit: "cover",
                          borderRadius: 14,
                          flexShrink: 0,
                        }}
                      />
                    )}

                    <div style={{ minWidth: 0, flex: 1 }}>
                      <div className="fw-bold">
                        {prodotto.name || "Prodotto"}
                      </div>

                      {prodotto.category && (
                        <div className="small text-muted">
                          {prodotto.category}
                        </div>
                      )}

                      {quantita && (
                        <div className="small text-muted mt-1">
                          {quantita}
                        </div>
                      )}

                      <div className="fw-bold mt-2">
                        {formatPrezzo(getPrezzo(prodotto))}
                      </div>
                    </div>

                    <div
                      className="text-secondary"
                      style={{ flexShrink: 0 }}
                    >
                      <Freccia />
                    </div>
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}
